import { useLocation, useNavigate } from "react-router-dom";
import { House, MagnifyingGlass, CalendarCheck, Heart, User } from "@phosphor-icons/react";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Footer } from "@/components/Footer";

const items = [
  { to: "/", label: "Home", icon: House, testid: "nav-home" },
  { to: "/explore", label: "Explore", icon: MagnifyingGlass, testid: "nav-explore" },
  { to: "/bookings", label: "Bookings", icon: CalendarCheck, testid: "nav-bookings" },
  { to: "/favorites", label: "Favorites", icon: Heart, testid: "nav-favorites" },
  { to: "/profile", label: "Profile", icon: User, testid: "nav-profile" },
];

const topLinks = [
  { to: "/", label: "Home" },
  { to: "/explore", label: "Explore" },
  { to: "/farmhouses", label: "Farmhouses" },
  { to: "/resorts", label: "Resorts" },
  { to: "/banquet-halls", label: "Banquet Halls" },
  { to: "/function-halls", label: "Function Halls" },
  { to: "/about", label: "About" },
  { to: "/contact", label: "Contact" },
];

export const CustomerLayout = ({ children }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, logout } = useAuth();

  const isActive = (to) => (to === "/" ? location.pathname === "/" : location.pathname.startsWith(to));

  return (
    <div className="min-h-screen bg-background">
      {/* Desktop top navigation */}
      <header className="sticky top-0 z-40 hidden border-b border-stone-200 bg-white/90 backdrop-blur-xl md:block">
        <div className="mx-auto flex max-w-6xl items-center justify-between px-6 py-3">
          <button onClick={() => navigate("/")} data-testid="desktop-brand" className="font-heading text-xl font-extrabold tracking-tight text-primary">
            Avinya <span className="font-semibold text-stone-800">Bookings</span>
          </button>
          <nav className="flex items-center gap-1">
            {topLinks.map((l) => (
              <button
                key={l.to}
                data-testid={`topnav-${l.label}`}
                onClick={() => navigate(l.to)}
                className={`rounded-full px-4 py-2 text-sm font-medium transition-colors ${isActive(l.to) ? "bg-accent text-primary" : "text-stone-600 hover:bg-stone-100"}`}
              >
                {l.label}
              </button>
            ))}
          </nav>
          <div className="flex items-center gap-2">
            <Button data-testid="nav-list-property" variant="outline" className="rounded-full border-primary text-primary hover:bg-accent" onClick={() => navigate("/list-your-property")}>List Your Property</Button>
            {user ? (
              <>
                <Button variant="ghost" className="rounded-full" onClick={() => navigate("/bookings")}>My Bookings</Button>
                <Button variant="outline" className="rounded-full" onClick={() => navigate("/profile")}>{user.name?.split(" ")[0]}</Button>
              </>
            ) : (
              <>
                <Button variant="ghost" className="rounded-full" onClick={() => navigate("/login")}>Login</Button>
                <Button className="rounded-full bg-primary hover:bg-green-900" onClick={() => navigate("/register")}>Register</Button>
              </>
            )}
          </div>
        </div>
      </header>

      <div className="mx-auto flex min-h-screen max-w-lg flex-col md:max-w-4xl">
        <main className="flex-1 pb-24 md:pb-12">{children}</main>
      </div>

      <Footer />

      {/* Mobile bottom navigation */}
      <nav
        className="fixed bottom-0 left-1/2 z-50 w-full max-w-lg -translate-x-1/2 border-t border-stone-200 bg-white/80 backdrop-blur-xl md:hidden pb-safe"
        data-testid="bottom-nav"
      >
        <div className="flex items-center justify-around px-2 py-2">
          {items.map((item) => {
            const active = isActive(item.to);
            const Icon = item.icon;
            return (
              <button
                key={item.to}
                data-testid={item.testid}
                onClick={() => {
                  if (!user && ["/bookings", "/favorites", "/profile"].includes(item.to)) navigate("/login");
                  else navigate(item.to);
                }}
                className={`flex flex-1 flex-col items-center gap-1 rounded-xl py-1.5 transition-colors duration-200 ${active ? "text-primary" : "text-stone-400 hover:text-stone-700"}`}
              >
                <Icon size={24} weight={active ? "fill" : "regular"} />
                <span className="text-[11px] font-medium">{item.label}</span>
              </button>
            );
          })}
        </div>
      </nav>
    </div>
  );
};
